/* La Caza Cali — sticky mini light switch visibility (round 6).
 *
 * Shows/hides the fixed pill from snippets/lacaza-light-switch-mini.liquid
 * (assets/lacaza-uplift.css handles the actual opacity/transform CSS via
 * the .lp-mini--visible class this file toggles). The pill's own on/off
 * state is bound by assets/lacaza-light-master.js like any other
 * [data-lights-master-switch] — this file only owns show/hide, plus its
 * own idle-fold (assets/lacaza-uplift.css .lp__switch--foldable).
 *
 * Round 5.3 owner fix — on a real phone the pill never appeared. Two
 * bugs, both from relying on IntersectionObserver on the header switch:
 *   1. A page can render MORE THAN ONE [data-lights-header] element
 *      (e.g. main-collection-banner.liquid's optional header alongside
 *      main-collection-product-grid.liquid's) where only one is
 *      actually visible at a given breakpoint (the other is
 *      display:none via CSS) — document.querySelector() grabbed
 *      whichever came first in the DOM regardless of which one was
 *      actually rendered, so its rect math was nonsense.
 *   2. IntersectionObserver callbacks are batched by the browser, not
 *      synchronous with scroll, and can lag noticeably.
 * Fixed by picking the VISIBLE header instance (offsetParent !== null)
 * and driving everything from a single passive scroll/resize listener +
 * rAF that reads real getBoundingClientRect() numbers every frame —
 * no IntersectionObserver in this file at all any more.
 *
 * Round 6 owner fix — the pill still went missing for long stretches of
 * the page. The show/hide gate used to require BOTH the header being
 * scrolled away AND the page's "last light-scene container" still being
 * on screen — on any page where that container is short (or far from
 * the header), there's a dead zone in between where the header is gone,
 * the scene container hasn't reached the viewport yet, and the pill
 * stayed hidden the whole time ("appears late"). It also vanished the
 * moment the scene container's bottom scrolled past, well before the
 * footer — ("disappears mid-page"). Fixed by hiding for exactly the two
 * moments called out by the owner: the header switch back in view, OR
 * the page footer already scrolling into view — visible for the entire
 * stretch in between, on every page shape.
 *
 * Round 6.1 owner fix — the pill still crashed into the right edge on
 * a real iPhone even after the pinned-footprint CSS fix. Root cause:
 * this theme has two infinite-scroll marquees (the footer ticker,
 * assets/lacaza-uplift.css .lacaza-marquee-track, and the top
 * announcement bar, blocks/ai_gen_block_7f18048.liquid) — both are
 * `width:max-content`/`fit-content` strips many times wider than any
 * screen, animated with `transform:translateX()`, each sitting inside
 * its own `overflow:hidden` wrapper. That's exactly the shape of a
 * known iOS Safari bug: `position:fixed; right:` anchors to the LAYOUT
 * viewport, and a wide `overflow:hidden`-clipped, transform-animated
 * box can still inflate the layout viewport's measured width even
 * though nothing is visibly scrollable — Chromium/desktop never
 * reproduce it because they don't have this quirk. Both marquees now
 * use `overflow: clip` at the source (fixes the actual cause) and
 * html/body carry an `overflow-x: clip` guard (assets/lacaza-uplift.css
 * "HORIZONTAL OVERFLOW GUARD"), but this file no longer trusts `right`
 * at all as a second, independent layer: positionPill() below places
 * the pill with an explicit `left` computed from
 * window.visualViewport (the one viewport iOS always gets right,
 * since it's what the user actually sees), re-run on every scroll,
 * resize, visualViewport resize/scroll, and fold/unfold. `right:auto`
 * is set alongside it so the CSS `right` value can never fight it.
 */
(function () {
  'use strict';

  var COLLISION_SELECTOR = '[class*="jdgm"], #gorgias-chat-container, [class*="stoq-"], .lacaza-pdp__sticky-atc.is-visible';
  var EDGE_GAP = 16;

  function findFooter() {
    return document.querySelector('footer.lacaza-footer') ||
      document.querySelector('footer[role="contentinfo"]') ||
      document.querySelector('footer');
  }

  // Positions the pill with an explicit left/right:auto, computed from
  // the VISUAL viewport (window.visualViewport) rather than trusting
  // CSS `right`, which anchors to the layout viewport instead — the
  // two can diverge on iOS Safari (see the file header comment).
  // Falls back to window.innerWidth (offsetLeft 0) where
  // visualViewport isn't supported.
  function positionPill(mini) {
    var vv = window.visualViewport;
    var vpWidth = vv ? vv.width : window.innerWidth;
    var vpOffsetLeft = vv ? vv.offsetLeft : 0;
    var pillWidth = mini.getBoundingClientRect().width || 152;
    var left = vpOffsetLeft + vpWidth - EDGE_GAP - pillWidth;
    mini.style.right = 'auto';
    mini.style.left = left + 'px';
  }

  // Any ancestor with transform/filter/backdrop-filter/perspective or
  // will-change:transform becomes the containing block for a
  // position:fixed descendant on EVERY browser (not just iOS) — the
  // element then anchors to THAT ancestor's box instead of the
  // viewport. Walk up from the pill and report/fix if found.
  function findFixedPositionHazard(el) {
    var node = el.parentElement;
    while (node && node !== document.body) {
      var cs = window.getComputedStyle(node);
      var backdrop = cs.backdropFilter || cs.webkitBackdropFilter || 'none';
      if (
        cs.transform !== 'none' ||
        cs.filter !== 'none' ||
        backdrop !== 'none' ||
        cs.perspective !== 'none' ||
        cs.willChange.indexOf('transform') !== -1
      ) {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  }

  function pickVisibleHeader() {
    var candidates = document.querySelectorAll('[data-lights-header]');
    for (var i = 0; i < candidates.length; i++) {
      if (candidates[i].offsetParent !== null) return candidates[i];
    }
    return null;
  }

  function computeCollisionOffset(mini) {
    var nodes = document.querySelectorAll(COLLISION_SELECTOR);
    var extra = 0;
    var vw = window.innerWidth;
    var vh = window.innerHeight;
    var MAX_OFFSET = 200;
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el === mini || mini.contains(el)) continue;
      var style = window.getComputedStyle(el);
      if (style.position !== 'fixed') continue;
      if (style.display === 'none' || style.visibility === 'hidden') continue;
      var rect = el.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) continue;
      var nearRight = rect.right > vw - 140;
      var nearBottom = rect.bottom > vh - 140;
      if (nearRight && nearBottom) {
        extra = Math.max(extra, Math.min(rect.height + 12, MAX_OFFSET));
      }
    }
    mini.style.setProperty('--lp-mini-offset', extra + 'px');
  }

  var MINI_FOLD_ENABLED = false;

  function initFold(mini) {
    var bar = mini.querySelector('.lp__bar');
    var sw = mini.querySelector('.lp__switch');
    if (!sw) return null;
    sw.classList.add('lp__switch--foldable');
    // Round 6.2: the folded control is a separate sibling <button>
    // (.lp__knob-fold) that crossfades in on top of the clip-path'd
    // switch — see assets/lacaza-uplift.css. It is only reachable /
    // announced while folded; tapping it unfolds AND toggles in one tap
    // by re-dispatching a click on the real switch (whose master
    // handler in assets/lacaza-light-master.js does the toggle).
    var knob = mini.querySelector('.lp__knob-fold');
    var timer = null;
    var visible = false;
    function setFolded(folded) {
      sw.classList.toggle('is-folded', folded);
      if (bar) bar.classList.toggle('lp__bar--folded', folded);
      if (knob) {
        knob.setAttribute('aria-hidden', folded ? 'false' : 'true');
        knob.tabIndex = folded ? 0 : -1;
      }
      // The pinned .lp__bar--compact width (assets/lacaza-uplift.css)
      // means folding no longer changes the pill's own footprint, but
      // re-running this is cheap and keeps it correct if that ever
      // changes again.
      positionPill(mini);
    }
    function clearFold() { if (timer) { window.clearTimeout(timer); timer = null; } }
    function scheduleFold() {
      clearFold();
      // Owner decision (Sept 20): the sticky pill stays EXPANDED — no
      // idle fold here. Only the PDP overlay folds
      // (assets/lacaza-light-pdp-dock.js). The fold plumbing is kept
      // wired so it can be re-enabled with this one flag.
      if (!MINI_FOLD_ENABLED) return;
      timer = window.setTimeout(function () {
        // No on/off gate — the folded knob is force-centered regardless
        // of state (assets/lacaza-uplift.css), so it reads correctly
        // whether the lamp scenes are currently on or off.
        if (visible) setFolded(true);
      }, 2500);
    }
    function wake() {
      setFolded(false);
      if (visible) scheduleFold();
    }
    ['pointerdown', 'pointermove', 'touchstart', 'mouseenter', 'click', 'focus'].forEach(function (evt) {
      sw.addEventListener(evt, wake, { passive: true });
    });
    if (knob) {
      knob.addEventListener('click', function (e) {
        e.preventDefault();
        wake();
        sw.click();
      });
    }
    // Owner fix (real iPhone): the fold timer used to start
    // unconditionally at page load, so by the time a shopper actually
    // scrolled the pill into view it could already be mid-fold or
    // folded — reading as "appears late" / broken. The timer now only
    // ever runs while the pill is actually visible, and is cancelled
    // (unfolded) the instant it's hidden again.
    return {
      onVisibilityChange: function (isVisible) {
        if (isVisible === visible) return;
        visible = isVisible;
        if (visible) {
          setFolded(false);
          scheduleFold();
        } else {
          clearFold();
          setFolded(false);
        }
      }
    };
  }

  function init() {
    var mini = document.querySelector('[data-lights-mini]');
    if (!mini) return;

    // Round 6.1 owner fix: a transformed/filtered/etc. ancestor turns
    // this position:fixed pill into position:absolute relative to
    // THAT ancestor, on every browser. Nothing in this theme should do
    // that to it today, but if a future section wrapper ever does,
    // move the pill out to be a direct child of body rather than
    // silently mispositioning it.
    var hazard = findFixedPositionHazard(mini);
    if (hazard && mini.parentElement !== document.body) {
      document.body.appendChild(mini);
    }

    // The footer is a page-wide singleton (never conditionally hidden
    // per breakpoint like the header can be), so it's safe to resolve
    // once here rather than re-querying every frame like the header.
    var footer = findFooter();

    var fold = initFold(mini);
    var sw = mini.querySelector('.lp__switch');

    var rafPending = false;

    function check() {
      rafPending = false;
      positionPill(mini);
      var header = pickVisibleHeader();
      var headerInView = header ? header.getBoundingClientRect().bottom >= 0 : false;
      var footerInView = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
      // Hide only when the header switch is back on screen OR the
      // footer has started scrolling into view — visible everywhere
      // else, so there's no dead zone mid-page.
      var show = !headerInView && !footerInView;
      if (show) computeCollisionOffset(mini);
      mini.classList.toggle('lp-mini--visible', show);
      if (fold) fold.onVisibilityChange(show);
      // Owner fix: the pill is hidden via opacity/pointer-events only
      // (assets/lacaza-uplift.css .lp--mini), which never removed it
      // from the keyboard tab order — a Tab-key user reached an
      // invisible, unfocusable-looking switch before the visible
      // header switch. Pull it out of the tab order while hidden.
      if (sw) sw.tabIndex = show ? 0 : -1;
    }

    function onScrollOrResize() {
      if (rafPending) return;
      rafPending = true;
      window.requestAnimationFrame(check);
    }

    // Owner fix: attached here, at DOMContentLoaded (init() itself is
    // only ever deferred behind DOMContentLoaded, never `load`, and
    // there is no IntersectionObserver anywhere in this file) — no
    // debounce either, rAF only coalesces multiple scroll events
    // within the same frame, it never delays past the next paint.
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });
    // Round 6.1: window's own scroll/resize track the LAYOUT viewport;
    // visualViewport fires its own resize/scroll (pinch-zoom, iOS
    // toolbar show/hide, on-screen keyboard) independent of those, so
    // positionPill() needs to re-run on those too.
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', onScrollOrResize);
      window.visualViewport.addEventListener('scroll', onScrollOrResize);
    }
    // Owner fix (round 6): bfcache returns (swipe-back on iOS Safari)
    // restore the page without re-running DOMContentLoaded, so a stale
    // visibility state could stick until the next scroll. Re-check
    // synchronously whenever the page is (re)shown.
    window.addEventListener('pageshow', check);
    check();

    window.setTimeout(function () {
      if (mini.classList.contains('lp-mini--visible')) computeCollisionOffset(mini);
    }, 2000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
